/**
 * PDF Export Utility for Financial Statements
 * Uses jsPDF with autoTable for professional financial document generation.
 */

import jsPDF from 'jspdf';
import autoTable, { type RowInput } from 'jspdf-autotable';

const COLORS = {
  primary: [30, 64, 115] as [number, number, number],    // Dark navy
  secondary: [100, 116, 139] as [number, number, number], // Slate
  accent: [16, 185, 129] as [number, number, number],     // Emerald
  danger: [239, 68, 68] as [number, number, number],      // Red
  black: [0, 0, 0] as [number, number, number],
  gray: [100, 116, 139] as [number, number, number],
  lightGray: [241, 245, 249] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

function formatCurrency(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (value < 0) return `($${formatted})`;
  return `$${formatted}`;
}

function addHeader(doc: jsPDF, title: string, subtitle: string, businessName?: string) {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Company name
  if (businessName) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(businessName, pageWidth / 2, 20, { align: 'center' });
  }

  // Statement title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text(title, pageWidth / 2, businessName ? 30 : 20, { align: 'center' });

  // Period / date subtitle
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(subtitle, pageWidth / 2, businessName ? 37 : 27, { align: 'center' });

  // Divider line
  const y = businessName ? 42 : 32;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);

  return y + 8;
}

function addFooter(doc: jsPDF) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
      20,
      pageHeight - 10,
    );
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }
}

// ============================================
// Income Statement / P&L
// ============================================

export interface IncomeStatementExpenseCategory {
  category: string;
  slug: string;
  taxDeductionClass: string | null;
  isInventory: boolean;
  amount: number;
  deductibleAmount: number;
  count: number;
}

export interface IncomeStatementData {
  period: { from: string; to: string };
  /** Revenue recognition basis the figures were computed on. */
  basis: 'cash' | 'accrual';
  revenue: {
    gross: number;
    taxCollected: number;
    byType: { type: string; amount: number; invoiceCount: number }[];
  };
  costOfGoodsSold: number;
  costOfGoodsSoldByCategory: IncomeStatementExpenseCategory[];
  grossProfit: number;
  operatingExpenses: {
    total: number;
    byCategory: IncomeStatementExpenseCategory[];
  };
  operatingIncome: number;
  netIncome: number;
  monthlyBreakdown: { month: string; monthName: string; revenue: number; expenses: number; netIncome: number }[];
}

/** 'other_income' is a real revenue line, not an invoice item type. */
function incomeRevenueLabel(type: string): string {
  if (type === 'other_income') return 'Other Income';
  return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ') + ' Revenue';
}

export function exportIncomeStatementPDF(data: IncomeStatementData, businessName?: string): void {
  const doc = new jsPDF();
  const fromDate = new Date(data.period.from).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const toDate = new Date(data.period.to).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const basisLabel = data.basis === 'accrual' ? 'Accrual Basis' : 'Cash Basis';
  let y = addHeader(doc, 'Income Statement', `For the Period ${fromDate} to ${toDate} \u00b7 ${basisLabel}`, businessName);

  // Revenue Section
  const revenueRows: (string | number)[][] = [];
  for (const r of data.revenue.byType) {
    revenueRows.push([`  ${incomeRevenueLabel(r.type)}`, formatCurrency(r.amount)]);
  }
  revenueRows.push([{ content: 'Gross Revenue', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.revenue.gross), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Revenue', 'Amount']],
    body: revenueRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // COGS
  if (data.costOfGoodsSold > 0) {
    autoTable(doc, {
      startY: y,
      body: [
        ...data.costOfGoodsSoldByCategory.map(c => [`  ${c.category}`, formatCurrency(c.amount)]),
        [{ content: 'Cost of Goods Sold', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.costOfGoodsSold), styles: { fontStyle: 'bold' } } as unknown as string],
        [{ content: 'Gross Profit', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.grossProfit), styles: { fontStyle: 'bold' } } as unknown as string],
      ],
      theme: 'plain',
      columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
      styles: { fontSize: 9, cellPadding: 2 },
      margin: { left: 20, right: 20 },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
  }

  // Operating Expenses
  const expenseRows: (string | number)[][] = [];
  for (const e of data.operatingExpenses.byCategory) {
    expenseRows.push([`  ${e.category}`, formatCurrency(e.amount)]);
  }
  expenseRows.push([{ content: 'Total Operating Expenses', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.operatingExpenses.total), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Operating Expenses', 'Amount']],
    body: expenseRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Net Income Summary
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1);
  doc.line(20, y, doc.internal.pageSize.getWidth() - 20, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    body: [
      [
        { content: 'Net Income (Before Tax)', styles: { fontStyle: 'bold', fontSize: 11 } } as unknown as string,
        { content: formatCurrency(data.netIncome), styles: { fontStyle: 'bold', fontSize: 11, textColor: data.netIncome >= 0 ? COLORS.accent : COLORS.danger } } as unknown as string,
      ],
    ],
    theme: 'plain',
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { cellPadding: 3 },
    margin: { left: 20, right: 20 },
  });

  addFooter(doc);
  doc.save(`Income_Statement_${data.period.from}_to_${data.period.to}.pdf`);
}

// ============================================
// Balance Sheet
// ============================================

export interface BalanceSheetData {
  asOfDate: string;
  assets: {
    current: { accountsReceivable: number; outstandingInvoiceCount: number; totalCurrent: number };
    fixed: {
      byType: { type: string; count: number; cost: number; accumulatedDepreciation: number; netBookValue: number }[];
      totalCost: number;
      totalAccumulatedDepreciation: number;
      netFixedAssets: number;
    };
    totalAssets: number;
  };
  liabilities: {
    byType: { type: string; count: number; balance: number; monthlyPayment: number }[];
    totalLiabilities: number;
  };
  equity: { ownersEquity: number };
  totalLiabilitiesAndEquity: number;
}

export function exportBalanceSheetPDF(data: BalanceSheetData, businessName?: string): void {
  const doc = new jsPDF();
  const asOf = new Date(data.asOfDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = addHeader(doc, 'Balance Sheet', `As of ${asOf}`, businessName);

  // Assets
  const assetRows: (string | number)[][] = [
    [{ content: 'Current Assets', styles: { fontStyle: 'bold' } } as unknown as string, ''],
    [`  Accounts Receivable (${data.assets.current.outstandingInvoiceCount} invoices)`, formatCurrency(data.assets.current.accountsReceivable)],
    [{ content: 'Total Current Assets', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.assets.current.totalCurrent), styles: { fontStyle: 'bold' } } as unknown as string],
    ['', ''],
    [{ content: 'Fixed Assets', styles: { fontStyle: 'bold' } } as unknown as string, ''],
  ];

  for (const a of data.assets.fixed.byType) {
    const label = a.type.charAt(0).toUpperCase() + a.type.slice(1).replace('_', ' ');
    assetRows.push([`  ${label} (${a.count})`, formatCurrency(a.netBookValue)]);
  }
  if (data.assets.fixed.totalAccumulatedDepreciation > 0) {
    assetRows.push(['  Less: Accumulated Depreciation', `(${formatCurrency(data.assets.fixed.totalAccumulatedDepreciation)})`]);
  }
  assetRows.push([{ content: 'Net Fixed Assets', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.assets.fixed.netFixedAssets), styles: { fontStyle: 'bold' } } as unknown as string]);
  assetRows.push(['', '']);
  assetRows.push([{ content: 'TOTAL ASSETS', styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string, { content: formatCurrency(data.assets.totalAssets), styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Assets', 'Amount']],
    body: assetRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Liabilities
  const liabilityRows: (string | number)[][] = [];
  for (const l of data.liabilities.byType) {
    const label = l.type.charAt(0).toUpperCase() + l.type.slice(1).replace('_', ' ');
    liabilityRows.push([`  ${label} (${l.count})`, formatCurrency(l.balance)]);
  }
  liabilityRows.push([{ content: 'Total Liabilities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.liabilities.totalLiabilities), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Liabilities', 'Amount']],
    body: liabilityRows.length > 1 ? liabilityRows : [['  No outstanding liabilities', '$0.00']],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Equity
  autoTable(doc, {
    startY: y,
    head: [["Owner's Equity", 'Amount']],
    body: [
      ["  Owner's Equity", formatCurrency(data.equity.ownersEquity)],
      [{ content: 'TOTAL LIABILITIES & EQUITY', styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string, { content: formatCurrency(data.totalLiabilitiesAndEquity), styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  addFooter(doc);
  doc.save(`Balance_Sheet_${data.asOfDate}.pdf`);
}

// ============================================
// Cash Flow Statement
// ============================================

export interface CashFlowData {
  period: { from: string; to: string };
  operating: {
    cashReceived: number;
    paymentCount: number;
    expensesPaid: number;
    expenseCount: number;
    taxPayments: number;
    netOperating: number;
  };
  investing: {
    purchases: { type: string; count: number; amount: number }[];
    totalPurchases: number;
    disposalProceeds: number;
    netInvesting: number;
  };
  financing: {
    estimatedLoanPrincipalPaid: number;
    loanInterestPaid: number; // informational — already counted in operating expensesPaid
    periodMonths: number;
    netFinancing: number;
  };
  netCashFlow: number;
  monthlyBreakdown: { month: string; monthName: string; cashIn: number; cashOut: number; netFlow: number }[];
}

export function exportCashFlowPDF(data: CashFlowData, businessName?: string): void {
  const doc = new jsPDF();
  const fromDate = new Date(data.period.from).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const toDate = new Date(data.period.to).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = addHeader(doc, 'Statement of Cash Flows', `For the Period ${fromDate} to ${toDate}`, businessName);

  // Operating Activities
  autoTable(doc, {
    startY: y,
    head: [['Operating Activities', 'Amount']],
    body: [
      [`  Cash Received from Customers (${data.operating.paymentCount} payments)`, formatCurrency(data.operating.cashReceived)],
      [`  Cash Paid for Expenses (${data.operating.expenseCount} expenses)`, `(${formatCurrency(data.operating.expensesPaid)})`],
      ['  Estimated Tax Payments', `(${formatCurrency(data.operating.taxPayments)})`],
      [{ content: 'Net Cash from Operating Activities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.operating.netOperating), styles: { fontStyle: 'bold', textColor: data.operating.netOperating >= 0 ? COLORS.accent : COLORS.danger } } as unknown as string],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Investing Activities
  const investRows: (string | number)[][] = [];
  for (const p of data.investing.purchases) {
    const label = p.type.charAt(0).toUpperCase() + p.type.slice(1).replace('_', ' ');
    investRows.push([`  ${label} Purchases (${p.count})`, `(${formatCurrency(p.amount)})`]);
  }
  if (data.investing.disposalProceeds > 0) {
    investRows.push(['  Proceeds from Asset Disposals', formatCurrency(data.investing.disposalProceeds)]);
  }
  if (investRows.length === 0) {
    investRows.push(['  No investing activity', '$0.00']);
  }
  investRows.push([{ content: 'Net Cash from Investing Activities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.investing.netInvesting), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Investing Activities', 'Amount']],
    body: investRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Financing Activities — principal repayment (estimated). Loan interest is shown
  // as a memo line; it's already counted in operating expenses above.
  autoTable(doc, {
    startY: y,
    head: [['Financing Activities', 'Amount']],
    body: [
      ['  Loan Principal Repayment (estimated)', `(${formatCurrency(data.financing.estimatedLoanPrincipalPaid)})`],
      [{ content: 'Net Cash from Financing Activities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.financing.netFinancing), styles: { fontStyle: 'bold' } } as unknown as string],
      ['  Memo: Loan interest paid (in operating)', formatCurrency(data.financing.loanInterestPaid)],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Net Cash Flow
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1);
  doc.line(20, y, doc.internal.pageSize.getWidth() - 20, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    body: [
      [
        { content: 'Net Change in Cash', styles: { fontStyle: 'bold', fontSize: 11 } } as unknown as string,
        { content: formatCurrency(data.netCashFlow), styles: { fontStyle: 'bold', fontSize: 11, textColor: data.netCashFlow >= 0 ? COLORS.accent : COLORS.danger } } as unknown as string,
      ],
    ],
    theme: 'plain',
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { cellPadding: 3 },
    margin: { left: 20, right: 20 },
  });

  addFooter(doc);
  doc.save(`Cash_Flow_Statement_${data.period.from}_to_${data.period.to}.pdf`);
}

// ============================================
// Multi-Year Forecast
// ============================================

export interface ForecastHistoryItem {
  year: number;
  revenue: number;
  expenses: number;
  capitalExpenditures: number;
  netIncome: number;
}

export interface ForecastProjection {
  year: number;
  revenue: number;
  expenses: number;
  capitalInvestment: number;
  totalDeductions: number;
  totalTax: number;
  effectiveRate: number;
  netIncomeAfterTax: number;
}

export interface ForecastPDFConfig {
  revenueGrowthRates: number[];
  expenseGrowthRates: number[];
  capitalInvestments: number[];
  filingStatus: string;
  entityType: string;
  state: string;
}

export function exportForecastPDF(
  history: ForecastHistoryItem[],
  projections: ForecastProjection[],
  config: ForecastPDFConfig,
  baseYear: number,
  businessName?: string,
): void {
  const doc = new jsPDF({ orientation: 'landscape' });
  const lastYear = projections[projections.length - 1]?.year || baseYear + 4;
  let y = addHeader(doc, 'Multi-Year Financial Forecast', `${baseYear + 1} \u2013 ${lastYear} Projections`, businessName);

  // Assumptions summary
  const assumptionRows: string[][] = [];
  for (let i = 0; i < projections.length; i++) {
    assumptionRows.push([
      (baseYear + i + 1).toString(),
      `${config.revenueGrowthRates[i] ?? 10}%`,
      `${config.expenseGrowthRates[i] ?? 5}%`,
      config.capitalInvestments[i] ? formatCurrency(config.capitalInvestments[i]) : '$0',
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [['Year', 'Revenue Growth', 'Expense Growth', 'Capital Investment']],
    body: assumptionRows,
    theme: 'grid',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
    margin: { left: 20, right: 20 },
    tableWidth: 'auto',
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // Main projection table
  const headers = ['Metric'];
  if (history.length > 0) {
    headers.push(`${baseYear} (Actual)`);
  }
  for (const p of projections) {
    headers.push(p.year.toString());
  }

  const latestActual = history[history.length - 1];
  const baseExpenses = latestActual ? latestActual.expenses : 0;

  const makeRow = (label: string, actualVal: number | null, projGetter: (p: ForecastProjection) => number): string[] => {
    const row = [label];
    if (history.length > 0) {
      row.push(actualVal !== null ? formatCurrency(actualVal) : '\u2014');
    }
    for (const p of projections) {
      row.push(formatCurrency(projGetter(p)));
    }
    return row;
  };

  const bodyRows = [
    makeRow('Revenue', latestActual?.revenue ?? null, p => p.revenue),
    makeRow('Operating Expenses', baseExpenses || null, p => p.expenses),
    makeRow('Capital Investment', latestActual?.capitalExpenditures ?? null, p => p.capitalInvestment),
    makeRow('Total Deductions', null, p => p.totalDeductions),
    makeRow('Total Tax', null, p => p.totalTax),
    // Effective Rate row
    (() => {
      const row = ['Effective Tax Rate'];
      if (history.length > 0) row.push('\u2014');
      for (const p of projections) {
        row.push(`${(p.effectiveRate * 100).toFixed(1)}%`);
      }
      return row;
    })(),
    makeRow('Net Income (After Tax)', latestActual?.netIncome ?? null, p => p.netIncomeAfterTax),
    makeRow('Quarterly Tax Reserve', null, p => p.totalTax / 4),
  ];

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: bodyRows,
    theme: 'grid',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } },
    styles: { cellPadding: 3, halign: 'right', overflow: 'linebreak' },
    didParseCell: (data) => {
      // Bold the Net Income row
      if (data.row.index === bodyRows.length - 2 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 10;
      }
      // Red for tax rows
      if ((data.row.index === 4 || data.row.index === 7) && data.section === 'body' && data.column.index > 0) {
        data.cell.styles.textColor = COLORS.danger;
      }
      // Green for net income
      if (data.row.index === bodyRows.length - 2 && data.section === 'body' && data.column.index > 0) {
        const val = parseFloat(String(data.cell.raw).replace(/[$,()]/g, ''));
        if (!isNaN(val)) {
          data.cell.styles.textColor = val >= 0 ? COLORS.accent : COLORS.danger;
        }
      }
      // Left-align first column
      if (data.column.index === 0) {
        data.cell.styles.halign = 'left';
      }
    },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Footer note
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.gray);
  doc.text(
    `Filing: ${config.filingStatus.replace(/_/g, ' ')} | Entity: ${config.entityType.replace(/_/g, ' ')}${config.state ? ` | State: ${config.state}` : ''}`,
    20, y,
  );
  doc.text(
    'Tax projections use 2025 federal brackets and are estimates only. Consult a tax professional.',
    20, y + 5,
  );

  addFooter(doc);
  doc.save(`Forecast_${baseYear + 1}_to_${lastYear}.pdf`);
}

// ============================================
// Schedule C / Form 1120 — Tax Form Exports
// ============================================
//
// Both forms share the same shape from the API: profile + income summary +
// per-deduction-class totals. Form-line mapping (Schedule C line numbers vs
// Form 1120 line numbers vs the "Other Deductions" Line 26 itemization for
// 1120) is owned by @bizsolutions/shared/tax/deduction-form-lines and applied
// at the renderer that built the data passed in here.

export interface TaxFormBusinessProfile {
  id: string;
  name: string;
  taxId: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/**
 * One line on the rendered form. `line` is the form line number (e.g., '8',
 * '20a', '24b' for Schedule C; '12', '18', '26' for Form 1120). `label` is
 * the IRS line description, optionally with our category name in parens.
 */
export interface TaxFormLine {
  line: string;
  label: string;
  amount: number;
  itemCount?: number;
}

/**
 * For Form 1120 only: the itemized "Other Deductions" attachment that backs
 * Line 26. Each row gets a friendly label + amount, summed into Line 26.
 */
export interface OtherDeductionRow {
  label: string;
  amount: number;
  itemCount: number;
}

export interface VehiclePdfRow {
  vehicleName: string;
  datePlacedInService: string | null;
  electedMethod: 'mileage' | 'actual' | 'mixed' | null;
  businessMiles: number;
  totalMiles: number;
  businessUsePct: number | null;  // Either explicit on the asset, or computed from miles. null = neither
  deductibleAmount: number;
}

export interface ScheduleCData {
  businessProfile: TaxFormBusinessProfile | null;
  taxYear: number;
  grossReceipts: number;
  cogs: number;
  grossProfit: number;
  expenseLines: TaxFormLine[];      // Already filtered/mapped to Schedule C lines, sorted by line number
  totalDeductions: number;
  netProfit: number;
  charitableExcluded?: { amount: number };  // Charitable not allowed on Schedule C — surfaced as a note
  vehicles?: VehiclePdfRow[];       // Schedule C Part IV vehicle detail
}

export interface Form1120Data {
  businessProfile: TaxFormBusinessProfile | null;
  taxYear: number;
  grossReceipts: number;
  cogs: number;
  grossProfit: number;
  namedLines: TaxFormLine[];        // 1120 lines that have a named slot (12, 13, 14, ..., 23, 24)
  otherDeductions: OtherDeductionRow[];  // The Line 26 itemized attachment
  totalOtherDeductions: number;
  totalDeductions: number;
  taxableIncome: number;
  federalTax: number;               // 21% flat for C-Corp
  vehicles?: VehiclePdfRow[];       // Optional supplementary vehicle detail (audit support)
}

function addProfileBlock(doc: jsPDF, profile: TaxFormBusinessProfile | null, startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...COLORS.lightGray);
  doc.rect(20, startY, pageWidth - 40, 22, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.gray);
  doc.text('BUSINESS NAME', 24, startY + 6);
  doc.text('EIN / TAX ID', pageWidth / 2 + 4, startY + 6);
  doc.text('CITY', 24, startY + 14);
  doc.text('STATE / ZIP', pageWidth / 2 + 4, startY + 14);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(10);
  doc.text(profile?.name || '—', 24, startY + 10);
  doc.text(profile?.taxId || 'Not set', pageWidth / 2 + 4, startY + 10);
  doc.text(profile?.city || '—', 24, startY + 18);
  doc.text(`${profile?.state || '—'} ${profile?.zip || ''}`, pageWidth / 2 + 4, startY + 18);

  return startY + 28;
}

/**
 * Render the Schedule C Part IV vehicle table (also reused on Form 1120 PDF
 * as supplementary audit-support detail). Emits a new page when the existing
 * `y` cursor is too low to fit the table cleanly.
 */
function addVehicleDetailSection(
  doc: jsPDF,
  vehicles: VehiclePdfRow[],
  startY: number,
  title: string,
  subtitle: string,
): number {
  // If we're past ~200mm on the page, start fresh — the table needs ~50mm.
  let y = startY;
  if (y > 220) {
    doc.addPage();
    y = 20;
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(title, 20, y);
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.gray);
  doc.text(subtitle, 20, y);
  y += 4;

  autoTable(doc, {
    startY: y,
    head: [['Vehicle', 'In Service', 'Method', 'Bus. Miles', 'Total Miles', 'Bus. Use %', 'Deductible']],
    body: vehicles.map(v => {
      const computedPct = v.businessUsePct ?? (v.totalMiles > 0
        ? Math.round((v.businessMiles / v.totalMiles) * 1000) / 10
        : null);
      return [
        v.vehicleName,
        v.datePlacedInService ? new Date(v.datePlacedInService).toLocaleDateString() : '—',
        v.electedMethod || '—',
        v.businessMiles.toLocaleString(),
        v.totalMiles > 0 ? v.totalMiles.toLocaleString() : '—',
        computedPct !== null ? `${computedPct}%` : '—',
        formatCurrency(v.deductibleAmount),
      ];
    }),
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 22 },
      2: { cellWidth: 18 },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 22, halign: 'right' },
      6: { cellWidth: 28, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  if (vehicles.some(v => v.electedMethod === 'mixed')) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(146, 64, 14);
    doc.text(
      'Note: One or more vehicles have mixed deduction methods (mileage + actual). IRS requires one method per vehicle per year.',
      20, y,
    );
    y += 5;
  }

  return y;
}

function addDisclaimerBlock(doc: jsPDF, y: number): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(254, 243, 199);  // amber-100
  doc.setDrawColor(245, 158, 11);   // amber-500
  doc.setLineWidth(0.3);
  doc.rect(20, y, pageWidth - 40, 18, 'FD');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(146, 64, 14);  // amber-800
  doc.text('FOR REFERENCE ONLY', 24, y + 6);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.text(
    'This is a summary of your recorded data, not an official IRS form. Consult your CPA or tax',
    24, y + 11,
  );
  doc.text('professional for actual filing. Print or share this PDF with your accountant.', 24, y + 15);
}

export function exportScheduleCPDF(data: ScheduleCData): void {
  const doc = new jsPDF();
  const businessName = data.businessProfile?.name;

  let y = addHeader(
    doc,
    'Schedule C — Profit or Loss From Business',
    `IRS Form 1040, Schedule C · Tax Year ${data.taxYear}`,
    businessName,
  );

  y = addProfileBlock(doc, data.businessProfile, y);

  // Part I — Income
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Part I — Income', 20, y);
  y += 4;

  const incomeRows: (string | { content: string; styles: object })[][] = [
    ['1', 'Gross receipts or sales', formatCurrency(data.grossReceipts)],
  ];
  if (data.cogs > 0) {
    incomeRows.push(['4', 'Cost of goods sold (Part III)', formatCurrency(-data.cogs)]);
  }
  incomeRows.push([
    { content: '7', styles: { fontStyle: 'bold' } },
    { content: 'Gross income', styles: { fontStyle: 'bold' } },
    { content: formatCurrency(data.grossProfit), styles: { fontStyle: 'bold' } },
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Line', 'Description', 'Amount']],
    body: incomeRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // Part II — Expenses
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Part II — Expenses', 20, y);
  y += 4;

  const expenseRows = data.expenseLines.length > 0
    ? data.expenseLines.map(l => [
        l.line,
        l.itemCount !== undefined ? `${l.label} (${l.itemCount})` : l.label,
        formatCurrency(l.amount),
      ])
    : [[{ content: 'No deductible expenses recorded for this tax year', colSpan: 3, styles: { halign: 'center', fontStyle: 'italic', textColor: COLORS.gray } } as unknown as string]];

  autoTable(doc, {
    startY: y,
    head: [['Line', 'Description', 'Amount']],
    body: expenseRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Charitable note (if applicable)
  if (data.charitableExcluded && data.charitableExcluded.amount > 0) {
    doc.setFillColor(219, 234, 254);  // blue-100
    doc.setDrawColor(59, 130, 246);   // blue-500
    doc.rect(20, y, doc.internal.pageSize.getWidth() - 40, 14, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);    // blue-800
    doc.text(
      `Charitable contributions: ${formatCurrency(data.charitableExcluded.amount)} — itemize on Schedule A (1040), not deductible on Schedule C.`,
      24, y + 9,
    );
    y += 18;
  }

  // Totals
  autoTable(doc, {
    startY: y,
    body: [
      [
        { content: '28', styles: { fontStyle: 'bold' } },
        { content: 'Total expenses', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(data.totalDeductions), styles: { fontStyle: 'bold' } },
      ],
      [
        { content: '31', styles: { fontStyle: 'bold', fillColor: COLORS.primary, textColor: COLORS.white } },
        { content: 'Net profit (or loss)', styles: { fontStyle: 'bold', fillColor: COLORS.primary, textColor: COLORS.white } },
        {
          content: formatCurrency(data.netProfit),
          styles: {
            fontStyle: 'bold',
            fillColor: COLORS.primary,
            textColor: data.netProfit >= 0 ? [134, 239, 172] : [254, 202, 202],  // light green / red
          },
        },
      ],
    ],
    theme: 'plain',
    bodyStyles: { fontSize: 11 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  addDisclaimerBlock(doc, y);

  // Schedule C Part IV — Vehicle Information (when applicable)
  if (data.vehicles && data.vehicles.length > 0) {
    addVehicleDetailSection(
      doc,
      data.vehicles,
      y + 22,  // leave room for the disclaimer block above
      'Part IV — Vehicle Information',
      'Required when claiming car/truck expenses on Line 9.',
    );
  }

  addFooter(doc);
  doc.save(`Schedule_C_${data.businessProfile?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Business'}_${data.taxYear}.pdf`);
}

export function exportForm1120PDF(data: Form1120Data): void {
  const doc = new jsPDF();
  const businessName = data.businessProfile?.name;

  let y = addHeader(
    doc,
    'Form 1120 — U.S. Corporation Income Tax Return',
    `IRS Form 1120 · Tax Year ${data.taxYear} · Flat 21% federal rate`,
    businessName,
  );

  y = addProfileBlock(doc, data.businessProfile, y);

  // Income
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Income', 20, y);
  y += 4;

  const incomeRows: RowInput[] = [
    ['1a', 'Gross receipts or sales', formatCurrency(data.grossReceipts)],
  ];
  if (data.cogs > 0) {
    incomeRows.push(['2', 'Cost of goods sold (Form 1125-A)', formatCurrency(-data.cogs)]);
  }
  incomeRows.push([
    { content: '3', styles: { fontStyle: 'bold' } },
    { content: 'Gross profit', styles: { fontStyle: 'bold' } },
    { content: formatCurrency(data.grossProfit), styles: { fontStyle: 'bold' } },
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Line', 'Description', 'Amount']],
    body: incomeRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // Deductions
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Deductions', 20, y);
  y += 4;

  const deductionRows: RowInput[] = data.namedLines.map(l => [
    l.line,
    l.label,
    formatCurrency(l.amount),
  ]);
  if (data.totalOtherDeductions > 0) {
    deductionRows.push([
      '26',
      'Other deductions (see itemized statement on next page)',
      formatCurrency(data.totalOtherDeductions),
    ]);
  }
  if (deductionRows.length === 0) {
    deductionRows.push([{
      content: 'No deductible expenses recorded for this tax year',
      colSpan: 3,
      styles: { halign: 'center', fontStyle: 'italic', textColor: COLORS.gray },
    }]);
  }

  autoTable(doc, {
    startY: y,
    head: [['Line', 'Description', 'Amount']],
    body: deductionRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // Totals
  autoTable(doc, {
    startY: y,
    body: [
      [
        { content: '27', styles: { fontStyle: 'bold' } },
        { content: 'Total deductions', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(data.totalDeductions), styles: { fontStyle: 'bold' } },
      ],
      [
        { content: '30', styles: { fontStyle: 'bold' } },
        { content: 'Taxable income (gross profit − total deductions)', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(data.taxableIncome), styles: { fontStyle: 'bold' } },
      ],
      [
        { content: '31', styles: { fontStyle: 'bold', fillColor: COLORS.primary, textColor: COLORS.white } },
        { content: 'Total tax (21% federal)', styles: { fontStyle: 'bold', fillColor: COLORS.primary, textColor: COLORS.white } },
        { content: formatCurrency(data.federalTax), styles: { fontStyle: 'bold', fillColor: COLORS.primary, textColor: [254, 202, 202] } },
      ],
    ],
    theme: 'plain',
    bodyStyles: { fontSize: 11 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  addDisclaimerBlock(doc, y);

  // Other Deductions Statement (page 2) — required attachment for 1120 Line 26
  if (data.otherDeductions.length > 0) {
    doc.addPage();
    let y2 = addHeader(
      doc,
      'Statement: Other Deductions (Form 1120, Line 26)',
      `Required itemization of deductions for ${businessName || 'Business'} · Tax Year ${data.taxYear}`,
      businessName,
    );

    autoTable(doc, {
      startY: y2,
      head: [['Description', 'Items', 'Amount']],
      body: [
        ...data.otherDeductions
          .slice()
          .sort((a, b) => b.amount - a.amount)
          .map(r => [r.label, String(r.itemCount), formatCurrency(r.amount)]),
        [
          { content: 'Total Other Deductions', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right' } },
          { content: formatCurrency(data.totalOtherDeductions), styles: { fontStyle: 'bold', fillColor: COLORS.lightGray } },
        ],
      ],
      theme: 'striped',
      headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
      },
      margin: { left: 20, right: 20 },
    });
    y2 = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
    addDisclaimerBlock(doc, y2);
  }

  // Vehicle detail (audit support — Form 1120 doesn't have a Part IV but the
  // data is useful when itemizing vehicle expenses in the Other Deductions
  // statement above).
  if (data.vehicles && data.vehicles.length > 0) {
    addVehicleDetailSection(
      doc,
      data.vehicles,
      999,  // Force new page (no risk of fitting after the Other Deductions Statement)
      'Vehicle Detail (Supplementary)',
      'Audit support for any vehicle expenses included in Other Deductions above.',
    );
  }

  addFooter(doc);
  doc.save(`Form_1120_${data.businessProfile?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Business'}_${data.taxYear}.pdf`);
}
